"use client"

import { Button } from "../../uiv7/Button"
import { type ReactNode, Suspense, useMemo } from "react"
import type { Route } from "next"
import {
  CallToAction,
  getInitialCtaString,
} from "@local/offer/src/modules/common/ButtonBuy/CallToAction"
import { getT } from "@local/i18n/src/t"
import { getLanguageForMarket } from "@local/i18n/src/languages"
import { buy } from "@local/i18n/src/catalogs/buy"
import { useHydrated } from "@local/utils/src/useHydrated"
import { useSearchParams } from "next/navigation"
import { trackAction } from "@local/tracking/src/trackActions"
import {
  rebuildUrlParams,
  stripForcedTrackingParams,
} from "@local/utils/src/rebuildUrlParams"
import type { Offer } from "@local/offer/src/types"

export const LandingPageCtaButton = ({
  market,
  channel,
  children,
  mirrorUrl,
  callToAction,
  product,
  prices,
  callToActionId,
}: {
  market: string
  channel: string
  children?: ReactNode
  mirrorUrl?: string
  callToAction?: string | null
  product?: Offer.PinProduct
  prices?: Offer.ListingPrices
  callToActionId?: string
}) => {
  const searchParams = useSearchParams()
  const itemId = searchParams?.get("item_id")

  // When item_id is present, the discount derives from a query param the
  // server render doesn't apply to the CTA text — feeding it into
  // CallToAction's first client render made the text diverge from the server
  // HTML (React #418 on LPs). Withhold only that case until after mount;
  // CallToAction re-parses its text post-mount and picks the discount up
  // then. Without item_id the discount comes solely from server-stable props
  // (preselected SKU + prices), so it's safe from the first paint — and
  // withholding it would advertise the template's hardcoded percentage
  // (e.g. -60%) until effects run.
  const hasMounted = useHydrated()

  // Calculate discount percentage based on modifier prices (mirrors HighlightPrice logic)
  const discount = useMemo(() => {
    if (!product) return undefined

    const originalPrice = prices?.[0]?.value ?? product.msrp
    const rawDiscount = prices?.[0]?.discountValue
    const defaultDiscountPrice =
      rawDiscount && rawDiscount > 0 ? rawDiscount : originalPrice

    // Try to find matching SKU from item_id in URL or preselected SKU
    let matchedModifierPrice: number | undefined
    let hasSpecificSkuMatch = false

    // Get all SKUs from the product
    const allSkus =
      product.type === "BUNDLE"
        ? product.skuLists.flatMap((skuList) => skuList.skus)
        : product.skus

    // Find SKU - first check item_id, then check for preselected
    const matchingSku = itemId
      ? allSkus.find(
          (sku) =>
            sku.legacyId === itemId ||
            sku.attributes.some(
              (attr) => attr.name === "payloadId" && attr.value === itemId
            ) ||
            sku.id === itemId ||
            sku.name === itemId
        )
      : allSkus.find((sku) => sku.isPreselected === true)

    if (matchingSku) {
      hasSpecificSkuMatch = true

      // Check if this SKU has a modifier price
      if (matchingSku.name && prices?.[0]?.modifiers?.length) {
        const matchedModifier = prices[0].modifiers.find((modifier) =>
          modifier.skus.includes(matchingSku.name as string)
        )

        if (matchedModifier) {
          matchedModifierPrice = matchedModifier.amount
        }
      }
    }

    // Determine final discount price
    let discountPrice: number

    if (hasSpecificSkuMatch) {
      discountPrice = matchedModifierPrice ?? defaultDiscountPrice
    } else {
      const lowestModifierPrice = prices?.[0]?.modifiers?.length
        ? prices[0].modifiers.reduce(
            (min, m) => (m.amount < min ? m.amount : min),
            prices[0].modifiers[0].amount
          )
        : undefined

      discountPrice =
        lowestModifierPrice && lowestModifierPrice < defaultDiscountPrice
          ? lowestModifierPrice
          : defaultDiscountPrice
    }

    return Math.round(((originalPrice - discountPrice) / originalPrice) * 100)
  }, [itemId, prices, product])

  // Build the href - if mirrorUrl is provided, rebuild with tracking params
  const href = useMemo(() => {
    let href: string = `/${market}/lp/${channel}/checkout`

    // Preserve item_id in checkout URL if present
    if (itemId) {
      href = `${href}?item_id=${itemId}`
    }

    if (mirrorUrl) {
      // Extract the path from mirrorUrl (everything after the domain)
      const url = new URL(mirrorUrl)
      const baseDomain = url.origin
      const path = url.pathname

      href = `${baseDomain}${rebuildUrlParams(
        stripForcedTrackingParams(searchParams),
        path
      )}`
    }

    return href
  }, [itemId, mirrorUrl, market, channel, searchParams])

  const handleCtaClick = () => {
    trackAction("cta_click", {
      component: "LandingPageCtaButton",
      destination: href,
      channel,
      cta_id: callToActionId ?? null,
    })
  }

  const actionLabel = getT(getLanguageForMarket(market), { buy })(
    "buy.selectSizeNow"
  )

  const ctaContent = children ? (
    children
  ) : (
    <Suspense
      fallback={
        <>{getInitialCtaString(callToAction, actionLabel, undefined, market)}</>
      }
    >
      <CallToAction
        input={callToAction}
        action={actionLabel}
        market={market}
        discount={hasMounted || !itemId ? discount : undefined}
      />
    </Suspense>
  )

  return (
    <Button
      as="link"
      variant="primary"
      href={href as Route}
      id={callToActionId}
      className="my-4 w-full uppercase hover:no-underline sm:text-xl"
      prefetch={true}
      data-cta="true"
      onClick={handleCtaClick}
    >
      {ctaContent}
    </Button>
  )
}
